// Copyright (c) 2023 Visual Intelligence LP. All rights reserved.
//
import * as React from "react";

import isEqual from 'lodash/isEqual';

import {
  ActionEventArgs,
  ColumnChooser,
  ColumnDirective,
  ColumnsDirective,
  Filter,
  GridComponent,
  Group,
  Resize,
  Sort,
  Reorder,
  ExcelExport,
  Edit,
  Inject,
  Toolbar,
  Freeze,
  ColumnModel
} from "@syncfusion/ej2-react-grids";

import { Tooltip } from '@syncfusion/ej2-popups'; 
import { SInformationDTO2 } from "@shared/models/sp-sheets";
import { SHEETS_HEADINGS_HEIGHT, GRID_LM_MEMBERS_COLUMN_HEADING_MAP, GROUPING_LM_MEMBERS, LM_MEMBERS_GRID_COLLAPSED_ITEMS, GRID_LM_MEMBER_COLUMN_NAMES } from "@shared/constants/sp-sheets";
import { withStyles } from "tss-react/mui";
import { MultiSelect, CheckBoxSelection, ComboBox } from "@syncfusion/ej2-react-dropdowns";
import { inchesPerMm, readFrLocalStorage } from "@shared/utils/common";
import SBase, { restoreCollapsedItems, saveCollapsedItems } from "../../SPContainer/components/SPSheet/components/SBase";
import { ROLES } from "@shared/constants/user";
import { ASSET_CONSTRUCTION_TYPE_LABELS } from "@shared/constants/asset";
import { MeasurementPanelDTO, MeasurementPanelMemberDTO } from "@shared/models/measurement-models";
import { Query } from '@syncfusion/ej2-data';
import { NotificationType, showNotification } from "@shared/components/Notification";
import ConfirmationDialog from "../ConfirmationDialog";

MultiSelect.Inject(CheckBoxSelection);

// prop interface for the main class.
export interface SMembersProps {
  className?: string;
  name: string;                                             // Actual site name
  panelsInfo: () => Array<MeasurementPanelDTO>;                   // Sites' panels and members
  membersInfo: () => Array<SInformationDTO2>;                     // Sites' detailed info such as name, type, connections...
  towerNames: Array<string>;                                // Tower names.
  memberTypes: {[key: number]: string};                     // Possible member types.
  boltSizes: {[key: number]: string};                       // Possible bolt sizes
  onAddMember: (args: MeasurementPanelMemberDTO) => Promise<number>;  // Add a new panel member
  onDeleteMembers: (args: Array<number>) => Promise<Array<number>>;   // Delete a list of panel members
  onUpdateMembers: (args: Array<MeasurementPanelMemberDTO>) => Promise<Array<number>>;   // Update a list of panel members
}

/*
  Display Lattice tower panels using syncfusion grid.
  Doc: https://ej2.syncfusion.com/documentation/grid/getting-started/
*/
class SMembers extends SBase<SMembersProps, {grouping: string, showDialog: boolean}> {
  private mainData: Array<MeasurementPanelMemberDTO>;
  private isCustomerUser: boolean;
  private headingMapObj: any;
  private gridGroupingStr: string;
  private gridCollapsedItemsStr: string;
  private unitMm: boolean; // true: mm, false: inch

  private siteData: {[key: string]: Object}[];
  private siteFields = { value: 'name', text: 'name' };
  private sitePanelData: {[key: string]: Object}[];
  private panelNumberFields = { value: 'panelNumber', text: 'panelNumber' };
  
  private dlgTitle: string;
  private dlgMessage: string;
  private toBeDeletedIds: Array<number>;
  private dlgItems: Array<string>;

  // Var to setup selection box in panel type cell.
  private siteComboElem: HTMLElement | undefined;
  private siteCombo: ComboBox | undefined;              // Handle to comboBox to select a site.
  private panelNumberComboElem: HTMLElement | undefined;
  private panelNumberCombo: ComboBox | undefined;       // Handle to comboBox to select a panel type.
  private memberTypeComboElem: HTMLElement | undefined; 
  private memberTypeCombo: ComboBox | undefined;        // Handle to comboBox to select a member type.
  private boltSizeComboElem: HTMLElement | undefined; 
  private boltSizeCombo: ComboBox | undefined;          // Handle to comboBox to select a bolt type.

  constructor(props: SMembersProps) {
    super(props);
    this.setupCopyAndPaste();
    this.headingMapObj = GRID_LM_MEMBERS_COLUMN_HEADING_MAP;
    this.gridGroupingStr = GROUPING_LM_MEMBERS;
    this.gridCollapsedItemsStr = LM_MEMBERS_GRID_COLLAPSED_ITEMS;

    this.setupColumnNCollapse(["100px", "200px", "70px", "120px", "120px", "70px", "70px", "70px", "70px", "70px", "100px"],
                             this.headingMapObj, this.gridCollapsedItemsStr);
    
    this.isCustomerUser = this.authStore.user.roles.every((role) => ROLES.customerUser == role);

    this.state = {grouping: readFrLocalStorage(this.mkUserKey(this.gridGroupingStr)), showDialog: false};

    // this.groupOptions = { columns: [], allowReordering: true, captionTemplate: this.myGroupCaptionTemplate.bind(this) }; // TBD: grouping.
    this.groupOptions = { columns: [], allowReordering: true };
    
    this.dlgTitle = '';
    this.dlgMessage = '';
    this.dlgItems = [];

    this.toBeDeletedIds = [];

    this.toolbar.unshift('Delete');
    this.toolbar.unshift({ text: 'Clone', prefixIcon: 'e-copy-1', id: 'clone', tooltipText: 'Clone the current record' });
    this.toolbar.unshift('Add');

    this.unitMm = true;
    this.toolbar.push({ text: 'Unit: mm', prefixIcon: 'e-change-scale-ratio', id: 'unit', tooltipText: 'Change the measurement unit (mm <=> inch)' });
  }

  // Translate construction type Id to the corresponding label.
  constructionType2name = (t: number | undefined) => {
    return t != undefined && (t in ASSET_CONSTRUCTION_TYPE_LABELS) ? ASSET_CONSTRUCTION_TYPE_LABELS[t] : 
                                                                 t ? t.toString() : '';
  }

  // Translate construction name to the corresponding type Id.
  constructionName2type = (n: string | undefined): number => {
    for(const propId in ASSET_CONSTRUCTION_TYPE_LABELS) {
      if(ASSET_CONSTRUCTION_TYPE_LABELS[propId] === n) {
        return Number(propId);
      }
    }
    return 0;
  }

  // TBD.
  myGroupCaptionTemplate(args) {
    //console.log('***** in myGroupCaptionTemplate:', args, args.items.childLevels);

    // let recs: Array<AssetVisitInfoDTO>;
    const key = '<TBD>';

    return (
      <div>{key}</div>
    );
  }

  buildSitePanel() {
    const { panelsInfo } = this.props;

    const tmpPanels: Array<SInformationDTO2> = [];    // Alloc a clean jobRequest info array.
    panelsInfo().forEach(panel => {
      tmpPanels.push({
        'name': panel.name,
        'panelNumber': panel.panelNumber
      });
    });
    this.sitePanelData = tmpPanels;
    // console.log('*** Initialize sitePanelData', this.sitePanelData);
  }

  // Triggered after the component loaded.
  // Load membersInfo from portal-api.
  // Note: Calls that need 'await' should be here.
  async componentDidMount(): Promise<void> {
    super.componentDidMount();
    const { membersInfo, panelsInfo } = this.props;

    this.mainData = [];

    membersInfo().forEach(member => {
      this.mainData.push({
        id: member.id,
        name: member.name,
        panelNumber: member.panelNumber,
        memberName: member.memberName,
        conType: member.conType,
        x: this.unitMm ? member.x : member.x * inchesPerMm,
        y: this.unitMm ? member.y : member.y * inchesPerMm,
        diameter: this.unitMm ? member.diameter : member.diameter * inchesPerMm,
        thickness: this.unitMm ? member.thickness : member.thickness * inchesPerMm,
        boltSize: member.boltSize,
        numberOfBolts: member.numberOfBolts
      });
    });

    this.buildSitePanel();

    const tmpSites: Array<SInformationDTO2> = [];    // Alloc a clean jobRequest info array.
    panelsInfo().forEach(panel => {
      if (!tmpSites.find(x => x['name'] === panel.name))
        tmpSites.push({
          'name': panel.name
        });
    });
    this.siteData = tmpSites;

    //console.log("*** (SPanels) clear loading to trigger a rerender.");
    this.setState((state) => ({ ...state, loading: false }));
  }

  refreshToolbarItemState() {
    // Add
    this.grid?.enableToolbarItems([this.mkUserKey('lattice-members_add')], !this.grid?.groupSettings.columns || this.grid?.groupSettings.columns.length == 0);

    // Delete & Clone
    const selectedTRowCellIndex = this.grid?.getSelectedRowCellIndexes();
    this.grid?.enableToolbarItems([this.mkUserKey('lattice-members_delete')], (selectedTRowCellIndex !== undefined && selectedTRowCellIndex.length > 0));
    this.grid?.enableToolbarItems(['clone'], (selectedTRowCellIndex !== undefined && selectedTRowCellIndex.length > 0));
  }

  // Triggered when a row is selected for editing.
  public handleActionBegin(args: any) {
    // const { panelsInfo } = this.props;
    // console.log('*** (SMembers) in actionBegin:', args)
    // console.log('* panelsInfo', panelsInfo());

    if (args.requestType === 'add' || args.requestType === 'beginEdit') {
      this.buildSitePanel();
    }
  }

  makeMetric(arg: MeasurementPanelMemberDTO): MeasurementPanelMemberDTO {
    if (this.unitMm) {
      return arg;
    }
    const metricObj = arg;
    metricObj.x  = arg.x / inchesPerMm;
    metricObj.y = arg.y / inchesPerMm;
    metricObj.diameter = arg.diameter / inchesPerMm;
    metricObj.thickness = arg.thickness / inchesPerMm;

    return metricObj;
  }

  public async addRecord(newData: MeasurementPanelMemberDTO): Promise<boolean> {
    const { onAddMember } = this.props;
    
    newData.id = -1; // Placeholder for the new item

    const newId = await onAddMember(newData);

    // Adding finishes, set the new id.
    if(newId !== 0)
    {
      newData.id = newId;
      // console.log('**** (SMembers) Main data:', this.mainData);
      return true;
    }
    return false;
  }

  public async editRecord(newData: MeasurementPanelMemberDTO, oldData: MeasurementPanelMemberDTO) {
    const { onUpdateMembers } = this.props;

    const editedPanelInfo: Array<MeasurementPanelMemberDTO> = []; // Alloc a clean site measurement array.
    editedPanelInfo.push(this.makeMetric(newData));
    const failedIds = await onUpdateMembers(editedPanelInfo);

    // Update failed, reverse cell to previous value.
    if(failedIds.length > 0)
    {
      // Get handler to the row in the datasource.
      const item = this.mainData.find(s => s.id == newData.id);
      if(item) {
        Object.assign(item, oldData);
        this.grid?.refreshColumns();
        this.collapsedItems = saveCollapsedItems(this.grid, this.mkUserKey(this.gridCollapsedItemsStr));
        this.grid?.refresh();
        restoreCollapsedItems(this.grid, this.collapsedItems); 
      }
    }
  }

  // Triggered after cell(s) updated (ie. deselected a row after editing the cell(s)).
  // 'args' should have the request type, new and old cell values.
  public async handleActionComplete(args: ActionEventArgs) {
    // console.log('*** (SMembers) in actionComplete:', args)

    const { requestType, type, data, action, previousData } = args;

    if(!this.isCustomerUser) {
      if(requestType === "save" && type === "actionComplete" && // check type and requestType
         !isEqual(data, previousData)) {                        // check if there are changes.
        if (action === 'add') {
          console.log('*** (SMembers) Add', data);
          const result = await this.addRecord(data as MeasurementPanelMemberDTO);
          
          // If fails, then delete the newly added record
          if (!result) {
            this.grid?.deleteRecord(this.columnNames[0], -1); // special id -1 was set in addRecord()
          }
        }
        else if (action === 'edit') {
          this.editRecord(data as MeasurementPanelMemberDTO, previousData as MeasurementPanelMemberDTO);
        }
        else {
          console.log("*** (SPanels) Unsupported action", action);
        }
      }
    }

    if (requestType === 'grouping' || requestType === 'ungrouping') {
      if(this.grid?.groupSettings.columns) {
        const grouping = this.grid.groupSettings.columns.join(",");
        // console.log('****** (SPanels) store grouping', grouping);
        localStorage.setItem(this.mkUserKey(this.gridGroupingStr), grouping);
        //this.initialLoad = true;  // uncomment if want to collapse the rows.
      }
    }

    this.refreshToolbarItemState();
  }

  handleCellSelected(args: any): void {
    super.handleCellSelected(args);
    this.refreshToolbarItemState();
  }
  
  handleHeaderCellInfo(args) {
    const valueArray = this.headingMapObj[ args.cell.column.field];
    if (valueArray != undefined)
    {
      const toolTip = valueArray['toolTip'];
      const tooltip: Tooltip = new Tooltip({ 
        content: toolTip,
        position: 'TopLeft',
        openDelay: 800
      }); 
      tooltip.appendTo(args.node); 
    }
  }

  makeCopiedMemberName(site: string, panel: string, member: string): string {
    const existingNames = this.mainData.filter(x => x.name === site && x.panelNumber === panel).map(x => x.memberName);
    let templateName = member + ' - Copy #';
    let testName = '';
    let copyNo = 0;
    do {
      testName = templateName + (++copyNo).toString();
      if (!existingNames || existingNames.indexOf(testName) == -1) {
        break;
      }
    } while (true);
    return testName;
  }

  switchUnit() {
    // console.log('*** (SMembers) change input unit to', this.unitMm ? 'mm' : 'inch');

    // Modify flag
    this.unitMm = !this.unitMm;

    // Change toolbar item text
    let toolbarItemUnit = this.grid?.toolbarModule.getToolbar().querySelector('#unit');
    if (toolbarItemUnit && toolbarItemUnit.lastChild) {
      (toolbarItemUnit.lastChild as HTMLElement).innerText = this.unitMm ? 'Unit: mm' : 'Unit: inch';
    }

    // Convert grid's data
    const { membersInfo } = this.props;
    this.mainData.forEach(item => {
      const member = membersInfo().find(x => x.id == item.id);
      if (member) {
        item.x = this.unitMm ? member.x : member.x * inchesPerMm;
        item.y = this.unitMm ? member.y : member.y * inchesPerMm;
        item.diameter = this.unitMm ? member.diameter : member.diameter * inchesPerMm;
        item.thickness = this.unitMm ? member.thickness : member.thickness * inchesPerMm;
      }
    })

    // Change the format of columns 'X', 'Y', 'Diameter', 'Thickness'
    const cols = [5, 6, 7, 8];
    cols.forEach(colIdx => {
      const heightCol = this.grid?.columns[colIdx] as ColumnModel;
      if (heightCol) {
        heightCol.format = this.unitMm ? 'N0' : 'N2';
      }
    })
    this.grid?.refreshColumns();
    this.grid?.refresh();
  }

  async handleToolbarClicked(args): Promise<boolean> {

    const selectedTRowCellIndex = this.grid?.getSelectedRowCellIndexes();
    const currentViewData = super.getCurrentViewData();

    if(args.item.text === 'Delete') {
      args.cancel = true; // Cancel the default delete action

      this.toBeDeletedIds = [];
      this.dlgTitle = 'Confirm the action;'
      this.dlgMessage = 'Do you really want to delete the following member(s)?';
      this.dlgItems = [];
      selectedTRowCellIndex?.forEach((item) => {
        if (currentViewData) {
          const deletedRow = currentViewData[item.rowIndex] as MeasurementPanelMemberDTO;
          this.toBeDeletedIds.push(deletedRow.id);
          this.dlgItems.push('Site: ' + deletedRow.name + ' - Panel #' + deletedRow.panelNumber + ' - Member: ' + deletedRow.memberName);
        }
      });

      if (this.toBeDeletedIds.length > 0) {
        // Show the confirmation dialog
        this.setState({showDialog: true});
      }
    }
    else if (args.item.text === 'Add') {
      if (this.grid?.groupSettings.columns && this.grid?.groupSettings.columns.length > 0) {
        args.cancel = true; // Cancel the default add action
        this.dlgTitle = 'Add a new record';
        this.dlgMessage = 'Cannot add a new row while being in grouping mode. Please remove all grouped columns first.';
        this.setState({showDialog: true});
      }
    }
    else {
      const handled = await super.handleToolbarClicked(args);

      if(!handled && args.item.id === 'paste') {
        const { onUpdateMembers, memberTypes, boltSizes } = this.props;
  
        const editedMembers: Array<MeasurementPanelMemberDTO> = []; // Alloc a clean site measurement panel member array.
        const backupData = new Map<number, any>(); // Backup data dictionary (memberId => data).
  
        // Possible values
        const possibleMemberTypes = Object.values(memberTypes);
        const possibleBoltSize = Object.values(boltSizes);
  
        // Paste the copied cell to the selected cells and add to the list (which will be sent to API).
        selectedTRowCellIndex?.forEach((item) => {
          item.cellIndexes.forEach(async colIndex => {
            if (colIndex == this.copiedCellIndex.col && // must be same column.
                (this.grid?.columns[colIndex] as ColumnModel).allowEditing) { // column must be editable.
              if (currentViewData) {
                const copiedValue = currentViewData[this.copiedCellIndex.row][this.columnNames[this.copiedCellIndex.col]];
                const editedRow = currentViewData[item.rowIndex] as MeasurementPanelMemberDTO;
                // console.log("*** (SMembers) Paste", copiedValue, "to cell (", item.rowIndex, ",", colIndex, "). Column name:", this.columnNames[colIndex]);
  
                // Copied value must be one of the possible values.
                if ((this.columnNames[colIndex] !== GRID_LM_MEMBER_COLUMN_NAMES.conType && this.columnNames[colIndex] !== GRID_LM_MEMBER_COLUMN_NAMES.boltSize) || 
                  (this.columnNames[colIndex] === GRID_LM_MEMBER_COLUMN_NAMES.conType && possibleMemberTypes.indexOf(copiedValue) !== -1) ||
                  (this.columnNames[colIndex] === GRID_LM_MEMBER_COLUMN_NAMES.boltSize && possibleBoltSize.indexOf(copiedValue) !== -1))
                {
                  // Add to the backup array
                  backupData.set(editedRow[GRID_LM_MEMBER_COLUMN_NAMES.id], editedRow[this.columnNames[colIndex]]);
  
                  // Paste the value and add to the array
                  const mainItem = this.mainData.find(x => x.id == (editedRow as MeasurementPanelMemberDTO).id);
                  if (mainItem) {
                    mainItem[this.columnNames[colIndex]] = copiedValue;
                    editedMembers.push(this.makeMetric(mainItem as MeasurementPanelMemberDTO));
                  }
                }
                else {
                  showNotification("Invalid value.", NotificationType.error);
                }
              }
            }
          });
        });
  
        // console.log("**** (SMembers) Edited rows:", editedMembers);
        // console.log("**** (SMembers) Backup rows:", backupData);
        // if (currentViewData) {
        //   console.log("******** (SMembers) Before updating. Current view:", currentViewData);
        // }
        // console.log("******** (SMembers) Before updating. Main data:", this.mainData);
  
        // Call API to update the target rows
        if (editedMembers.length > 0) {
          const failedIds = await onUpdateMembers(editedMembers); // Do not remove 'await' as suggested by lint packages.
          // Update failed, reverse cell to previous value.
          if (failedIds.length > 0)
          {
            failedIds.forEach(id => {
              // Get handler to the row in the datasource.
              const item = this.mainData.find(s => s.id == id);
              if(item) {
                // Restore data
                var oldData = backupData.get(id);
                if (oldData)
                {
                  console.log("*** (SMembers) Restore data to id#", id);
                  item[this.columnNames[this.copiedCellIndex.col]] = oldData;
                }
              }
            })
          }
  
          // console.log("******** (SPanels) After updating. Current data:", this.mainData);
  
          // Update the view if there's at least one successful update
          if (failedIds.length != editedMembers.length) {
            // Refresh grid and restore collapse state.
            this.grid?.refreshColumns(); // This seems necessary even though there is another refresh later.
            this.collapsedItems = saveCollapsedItems(this.grid, this.mkUserKey(LM_MEMBERS_GRID_COLLAPSED_ITEMS));
            this.grid?.refresh();
            restoreCollapsedItems(this.grid, this.collapsedItems);
          }
        }
      }
      else if (!handled && args.item.id === 'unit') {
        this.switchUnit();
      }
      else if (!handled && args.item.id === 'clone') {
        selectedTRowCellIndex?.forEach((item, idx) => {
          // Use the first selected row as template
          if (idx === 0 && currentViewData) {
            const copiedRec = { ...currentViewData[item.rowIndex] as MeasurementPanelMemberDTO };
            copiedRec.memberName = this.makeCopiedMemberName(copiedRec.name, copiedRec.panelNumber, copiedRec.memberName);
            this.grid?.addRecord(copiedRec);
          }
        });
      }
    }
    return true;
  }

  componentWillUnmount() {
    super.componentWillUnmount()

    // Store collapsed items for persistence.
    this.collapsedItems = saveCollapsedItems(this.grid, this.mkUserKey(this.gridCollapsedItemsStr));
  }

  // Set cell background color based on exception code
  customizeCell(args) {
    // const item = args.data as AssetVisitInfoDTO & {site: string, typeName: string, deltaName: string};
    // if (args.cell && item?.deltaName) {
    //   const field = args.column.field;
    //   if (field == 'name') {
    //     args.cell.style.backgroundColor = DELIVERABLE_EXCEPTION_CODE_TO_COLOR['moderate'];
    //   }
    // }
  }

  getDialogTitle(): string {
    return this.dlgTitle;
  }

  getDialogMessage(): string {
    return this.dlgMessage;
  }

  getDialogItems(): Array<string> {
    return this.dlgItems;
  }

  resetDialogParams() {
    // Reset
    this.dlgTitle = '';
    this.dlgMessage = '';
    this.dlgItems = [];
  }

  onCloseDialog() {
    // Close the confirmation dialog
    this.setState({ showDialog: false });
    this.resetDialogParams();
  }

  async onSubmitDialog() {
    // Close the confirmation dialog
    this.setState({ showDialog: false });

    // Call API to delete the records
    const { onDeleteMembers } = this.props;
    
    if (this.toBeDeletedIds.length > 0) {
      const deletedIds = await onDeleteMembers(this.toBeDeletedIds);
      if (deletedIds.length !== 0) {
        deletedIds.forEach((id) => {
          console.log('*** Delete member#', id);
          this.grid?.deleteRecord(this.columnNames[0], id);
        })
      }
      this.toBeDeletedIds = [];
    }
    this.resetDialogParams();
  }

  // ========================== Site ComboBox Control ==========================
  private createSiteCombo = () => {
    this.siteComboElem = document.createElement('combo');
    return this.siteComboElem;
  };
  
  private destroySiteCombo = () => {
    this.siteCombo?.destroy();
  };
  
  private readSiteCombo = () => {
    return this.siteCombo?.text;
  };

  private onChangeSite(args: any): void {
    if (this.panelNumberCombo) {
      const siteName = args.value;
      console.log('*** onChangeSite: refresh the panelNumber list for site', siteName);
      let siteQuery = new Query().where(
        'name',
        'equal',
        siteName
      );
      this.panelNumberCombo.query = siteQuery;
      this.panelNumberCombo.enabled = true;
      this.panelNumberCombo.dataBind();
    }
  }
  
  private writeSiteCombo = (args) => {
    let comboBoxValue = args.rowData[args.column.field];
    
    this.siteCombo = new ComboBox({
      value: comboBoxValue,
      dataSource: this.siteData,
      fields: this.siteFields,
      change: this.onChangeSite.bind(this),
    });
    this.siteCombo.appendTo(this.siteComboElem);
  };
  // ===========================================================================

  // ========================== PanelNumber ComboBox Control ==========================
  private createPanelNumberCombo = () => {
    this.panelNumberComboElem = document.createElement('combo');
    return this.panelNumberComboElem;
  };
  
  private destroyPanelNumberCombo = () => {
    this.panelNumberCombo?.destroy();
  };
  
  private readPanelNumberCombo = () => {
    return this.panelNumberCombo?.text;
  };
  
  private writePanelNumberCombo = (args) => {
    let comboBoxValue = args.rowData[args.column.field];
    
    this.panelNumberCombo = new ComboBox({
      value: comboBoxValue,
      dataSource: this.sitePanelData,
      fields: this.panelNumberFields,
      enabled: (this.siteCombo?.text) ? true : false,
      query: new Query().where('name', 'equal', this.siteCombo?.text ? this.siteCombo?.text : '')
    });
    this.panelNumberCombo.appendTo(this.panelNumberComboElem);
  };
  // ===========================================================================

  render() {
    const { towerNames, memberTypes, boltSizes } = this.props;
    const classes = withStyles.getClasses(this.props);
  
    // console.log("*** render sMember: ", this.mainData, ", bolt:", boltSizes);

    const possibleMemberTypes = Object.values(memberTypes);
    const possibleBoltSize = Object.values(boltSizes);

    let columnIndex = 0;
    return (
      <div className={classes.root}>
        <GridComponent
          dataSource={this.mainData}
          ref={(g) => (this.grid = g)}
          id={this.mkUserKey('lattice-members')} // enablePersistence uses this as key to store the grid settings.
      
          // Basic configs
          editSettings={{ allowEditing: !this.isCustomerUser, allowAdding: !this.isCustomerUser, allowDeleting: !this.isCustomerUser }}
          selectionSettings={{ mode: 'Cell', type: 'Multiple', enableToggle: false }}
          toolbar={this.toolbar} 
          showColumnChooser={true}                // Allow showing/hiding cols
          gridLines='Both'                        // Show horizontal and vertical grid lines.
          height={Number(window.innerHeight - SHEETS_HEADINGS_HEIGHT)}

          // Event handlers. Bind 'this' so they can access props and state.
          //dataStateChange={this.handleDataStateChange.bind(this)}
          //dataSourceChanged={this.handleDataSourceChanged.bind(this)}
          dataBound={this.handleDataBound.bind(this)}

          actionBegin={this.handleActionBegin.bind(this)}
          actionComplete={this.handleActionComplete.bind(this)}
          headerCellInfo={this.handleHeaderCellInfo.bind(this)}
          toolbarClick={this.handleToolbarClicked.bind(this)}
          cellSelected={this.handleCellSelected.bind(this)}
          queryCellInfo={this.customizeCell.bind(this)}

          //
          // Additional features.
          //

          // wrap header line if does not fit.
          allowTextWrap={true}
          textWrapSettings={{ wrapMode: 'Header' }} 

          // pagination.
          //allowPaging={true}  
          //pageSettings={{ pageSize: SHEETS_PAGE_SIZE }}

          // sorting rows.
          allowSorting={true}

          // Resize cols.
          allowResizing={true}

          // Reorder columns
          allowReordering={true}

          // Excel export
          allowExcelExport={true}

          // Apparently, setting up grouping in one of the tabs will enable it in other tabs.  So must explicitly set to false if the feature is not used.
          allowGrouping={true}
          groupSettings={this.groupOptions}

          // filter rows.
          allowFiltering={true}
          filterSettings={{ type: 'CheckBox' }} // Note: it's possible to setup and show a 'filterTemplate' control for the filter.

          // Keep grid settings persistence.
          enablePersistence={true}
        >
          <ColumnsDirective> 
            <ColumnDirective // ID 0
              field={this.columnNames[columnIndex]}
              allowEditing={false}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              isPrimaryKey={true}
              customAttributes={{ class: "customcss" }}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
            />
            { columnIndex++ }

            <ColumnDirective // Site 1
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              isPrimaryKey={false}
              customAttributes={{ class: "customcss" }}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              // edit={this.BuildComboParams(this.siteComboElem, this.siteCombo, towerNames, this.columnNames[columnIndex], this.handleSiteChange.bind(this))}
              edit={{create: this.createSiteCombo.bind(this), destroy: this.destroySiteCombo.bind(this), read: this.readSiteCombo.bind(this), write: this.writeSiteCombo.bind(this)}}
            />
            { columnIndex++ }

          <ColumnDirective // Panel# 2
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              customAttributes={{ class: "customcss" }}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              // edit={this.BuildComboParams(this.panelNumberComboElem, this.panelNumberCombo, this.possiblePanelNumbers, this.columnNames[columnIndex])}
              edit={{create: this.createPanelNumberCombo.bind(this), destroy: this.destroyPanelNumberCombo.bind(this), read: this.readPanelNumberCombo.bind(this), write: this.writePanelNumberCombo.bind(this),
              }}
            />
            { columnIndex++ }

            <ColumnDirective // Member name 3
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              customAttributes={{ class: "customcss" }}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
            />
            { columnIndex++ }

            <ColumnDirective // Type 4
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              edit={this.BuildComboParams(this.memberTypeComboElem, this.memberTypeCombo, possibleMemberTypes, this.columnNames[columnIndex])}
            />
            { columnIndex++ }
            
            <ColumnDirective  // X 5
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              type='Number'
              format='N0'
            />
            { columnIndex++ }

            <ColumnDirective // Y 6
              field={this.columnNames[columnIndex]}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              allowEditing={true}
              visible={this.columnVisibility[columnIndex]}
              allowGrouping={true}
              filter={{type: 'Excel'}}
              format='N0'
            />
            { columnIndex++ }

            <ColumnDirective // Diameter 7
              field={this.columnNames[columnIndex]}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              allowEditing={true}
              visible={this.columnVisibility[columnIndex]}
              allowGrouping={true}
              filter={{type: 'Excel'}}
              format='N0'
            />
            { columnIndex++ }

            <ColumnDirective // Thickness 8
              field={this.columnNames[columnIndex]}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              allowEditing={true}
              visible={this.columnVisibility[columnIndex]}
              allowGrouping={true}
              filter={{type: 'Excel'}}
              format='N0'
            />
            { columnIndex++ }

            <ColumnDirective // Bolt size 9
              field={this.columnNames[columnIndex]}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              allowEditing={true}
              visible={this.columnVisibility[columnIndex]}
              allowGrouping={true}
              filter={{type: 'Excel'}}
              edit={this.BuildComboParams(this.boltSizeComboElem, this.boltSizeCombo, possibleBoltSize, this.columnNames[columnIndex])}
            />
            { columnIndex++ }

            <ColumnDirective // Number of bolts 10
              field={this.columnNames[columnIndex]}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              allowEditing={true}
              visible={this.columnVisibility[columnIndex]}
              allowGrouping={true}
              filter={{type: 'Excel'}}
            />
          </ColumnsDirective>
          <Inject services={[Freeze, Edit, Toolbar, Sort, Filter, ColumnChooser, Resize, Group, Reorder, ExcelExport]} />
        </GridComponent>

        {this.state.showDialog &&
        <ConfirmationDialog
          title={this.getDialogTitle.bind(this)}
          message={this.getDialogMessage.bind(this)}
          items={this.getDialogItems.bind(this)}
          showDialog={this.state.showDialog}
          onClose={this.onCloseDialog.bind(this)}
          onSubmit={this.onSubmitDialog.bind(this)}
        />
        }
      </div>
    )
  }
}

const SMembersStyled = withStyles(
  SMembers, 
  (theme, props) => ({
    "root": {
      paddingTop: theme.spacing(5)
    }
  })
);

export default SMembersStyled;